// Wildcard certificate confirmation V1.4; 2026-10-30 00:11:36 CST; parent 518cf6c2934e93d3ca74cdcdc197c61ae7d7f390.
// Console 0.1.140; 2026-10-08 17:24 CST; parent 83db177f6d7c30135ab80204b792bebb67d9d79d.
// Account password setup V1.0; 2026-10-07 13:29:02 CST; parent 98f2abb400ae82aeb79450073a06825fca745614.
// Geo distribution V1.0; 2026-10-07 09:05:47 CST; parent dcebd36069b2ddb3e45e586f2c90a0214b9a2a4f.
// HotX Console 0.1.119；实际修改时间：2026-09-28 14:11:16 CST；父基线：f5639e9c85e75bbd0c945e00e403baf3f6768958；变更：TRAFFIC-NODE-VISIBILITY-20260928。
// Root301 console V1.0 / v0.1.106; 2026-09-15 22:13:20 CST; parents 2babea5a62fff79dcdcc107cd525702ace774b11 / 46bed30ba11c7aa4c37387ae28f698f8a4031b9a.
// HotX Console 0.1.100 套餐域名组合 API；实际修改时间：2026-09-15 02:31:52 CST；功能源码基线：a9b8b6110efa5c3852b4719e25a0d3d45e99d5f6。
/* HotX 域名 API 备份注释
 * 注释时间：2026-08-29 20:52 CST
 * 这里封装域名管理相关接口：域名列表、创建域名、更新域名、删除域名、发布配置、接入状态、源站配置等。
 * 同一个 API 文件也包含证书接口，打包域名管理备份时保留完整文件，避免接口上下文断裂。
 */

/* HotX 证书 API 备份注释
 * 注释时间：2026-08-29 20:52 CST
 * 这里封装证书管理相关接口：列表、申请托管证书、重新生成记录值/重新签发、验证 DNS、下载、自动续期等。
 * 前端页面展示中文失败原因，但这里仍保持接口字段透传，便于后端日志和排查保持完整。
 */

// HotX Console 0.1.116 / 2026-09-20 11:30 CST / parent ec8332a397e5e5bccbe103d4bcd82792ba6b2b1a：增加当前账户用户名保存契约。
import type {
  AccessSummary,
  TrafficEvidence,
  AcmeOrderResponse,
  AccountProfile,
  AuthTokenResponse,
  CacheProfile,
  CacheOperation,
  CacheRule,
  Certificate,
  CertificateAutoReissueRequest,
  CertificateCoverageResponse,
  CertificateDecision,
  CertificateDecisionRequest,
  CertificateDownloadRequest,
  CertificateMatchResponse,
  ConsolePlanResponse,
  ConsoleHttpClient,
  CountryStat,
  CustomerDnsResponse,
  Domain,
  DomainActiveConfig,
  DomainCreatePayload,
  DomainDraft,
  DomainDraftCreatePayload,
  DomainSaveResponse,
  RootRedirectSnapshot,
  RootRedirectRecheckResponse,
  ExportTask,
  FreeCdnApplyRequest,
  FreeCdnApplyResponse,
  GoogleAuthorizationUrlRequest,
  GoogleAuthorizationUrlResponse,
  GoogleLoginPayload,
  HighRiskActionRequest,
  IpCollection,
  IpCollectionBinding,
  IpCollectionBindingRequest,
  IpCollectionCreateRequest,
  LoginPayload,
  ManagedCertificateRequest,
  NodeAnalyticsResponse,
  OperationLog,
  Origin,
  OriginCheckReport,
  PortalDomainsPage,
  PortalOverview,
  PublishHistory,
  PublishLog,
  PublishPackage,
  PublishRelease,
  RegisterPayload,
  ServiceStatus,
  LogPage,
  CountryPolicy,
  SecurityLog,
  SecurityIpRule,
  SecurityIpRuleRequest,
  SecurityPolicySimulationRequest,
  SecurityPolicySimulationResponse,
  SecurityStrategy,
  SecurityStrategyConfiguration,
  SecurityStrategyDeleteResponse,
  SecurityStrategyMutationResponse,
  SecurityStrategySimulationResponse,
  StatusCodeStat,
  TopIpStat,
  UploadCertificateRequest,
  UpdateCertificateRequest
} from './types';

/*
 * Console API contract map:
 * - /api/v1/auth/* is customer-facing auth only.
 * - /api/v1/domains and /api/v1/portal/pages/* are customer-scoped and must never leak another customer.
 * - Certificate issuance, domain onboarding, publish, cache, security, and metering remain separate business flows.
 * - Keep this client thin; do not hide missing backend capabilities with fake frontend state.
 */
export function createConsoleApi(http: ConsoleHttpClient) {
  const serviceStatus = () => http.get<ServiceStatus>('/api/v1/account/service-status');
  const currentPlan = async (): Promise<ConsolePlanResponse> => {
    return serviceStatusToPlan(await serviceStatus());
  };
  const applyFreeCdn = async (body: FreeCdnApplyRequest): Promise<FreeCdnApplyResponse> => {
    void body;
    const plan = await currentPlan();
    const currentPlanLike = plan.currentPlan || fallbackPlan();
    const isFree = currentPlanLike.snapshot.planType === 'FREE';
    return {
      action: isFree ? 'KEEP_FREE_PLAN' : 'KEEP_EXISTING_FORMAL_PLAN',
      shouldOpenFreePlan: false,
      shouldEnterDomainFlow: true,
      shouldDowngradePlan: false,
      reasonCode: isFree ? 'FREE_PLAN_EXISTS' : 'FORMAL_PLAN_EXISTS',
      plan: currentPlanLike,
      quota: plan.quota
    };
  };
  const emptyIpCollectionBinding = (): IpCollectionBinding => ({
    whitelistCollections: [],
    blacklistCollections: []
  });

  return {
    auth: {
      sendRegisterCode: (email: string) => http.post<void>('/api/v1/auth/register/email-code', { email }),
      register: (body: RegisterPayload) => http.post<AuthTokenResponse, RegisterPayload>('/api/v1/auth/register', body),
      login: (body: LoginPayload) => http.post<AuthTokenResponse, LoginPayload>('/api/v1/auth/login', body),
      googleAuthorizationUrl: (body: GoogleAuthorizationUrlRequest) =>
        http.post<GoogleAuthorizationUrlResponse, GoogleAuthorizationUrlRequest>(
          '/api/v1/auth/google/authorization-url',
          body
        ),
      googleLogin: (body: GoogleLoginPayload) =>
        http.post<AuthTokenResponse, GoogleLoginPayload>('/api/v1/auth/google/login', body),
      logout: () => http.post<void>('/api/v1/auth/logout'),
      logoutAll: () => http.post<void>('/api/v1/auth/logout-all'),
      sendPasswordResetCode: (email: string) => http.post<void>('/api/v1/auth/password/reset-code', { email }),
      resetPassword: (body: { email: string; verificationCode: string; newPassword: string }) =>
        http.post<void>('/api/v1/auth/password/reset', body)
    },
    account: {
      passwordState: () => http.get<{ state: 'SET' | 'UNSET' | 'UNKNOWN'; email: string }>('/api/v1/account/password-state'),
      profile: () => http.get<AccountProfile>('/api/v1/account/profile'),
      updateUsername: (username: string) =>
        http.put<{ username: string; customerName: string }>('/api/v1/account/username', { username }),
      serviceStatus,
      changePassword: (body: { currentPassword: string; newPassword: string }) =>
        http.put<AuthTokenResponse>('/api/v1/account/password', body),
      sendEmailCode: (body: { currentPassword: string; newEmail: string }) =>
        http.post<void>('/api/v1/account/email/change-code', body),
      changeEmail: (body: { currentPassword: string; newEmail: string; verificationCode: string }) =>
        http.put<AuthTokenResponse>('/api/v1/account/email', body)
    },
    portal: {
      overview: () => http.get<PortalOverview>('/api/v1/portal/pages/overview'),
      domains: () => http.get<PortalDomainsPage>('/api/v1/portal/pages/domains'),
      domain: (domainId: number) => http.get(`/api/v1/portal/pages/domains/${domainId}`),
      origins: (domainId: number) => http.get(`/api/v1/portal/pages/domains/${domainId}/origins`),
      https: (domainId: number) => http.get(`/api/v1/portal/pages/domains/${domainId}/https`),
      cache: (domainId: number) => http.get(`/api/v1/portal/pages/domains/${domainId}/cache`),
      security: (domainId: number) => http.get(`/api/v1/portal/pages/domains/${domainId}/security`),
      countryPolicy: (domainId: number) => http.get(`/api/v1/portal/pages/domains/${domainId}/country-policy`),
      originWhitelist: (domainId: number) =>
        http.get(`/api/v1/portal/pages/domains/${domainId}/origin-whitelist`),
      logs: (page = 1, pageSize = 20) =>
        http.get('/api/v1/portal/pages/logs', { params: { page, pageSize } }),
      account: () => http.get('/api/v1/portal/pages/account')
    },
    plan: {
      current: currentPlan,
      freeRenew: () => http.post<ConsolePlanResponse>('/api/v1/console/plan/free-renew')
    },
    freeCdn: {
      apply: applyFreeCdn
    },
    domains: {
      list: () => http.get<Domain[]>('/api/v1/domains'),
      search: (params: Record<string, unknown>) => http.get('/api/v1/domains/search', { params }),
      create: (payload: string | DomainCreatePayload) =>
        http.post<Domain>('/api/v1/domains', typeof payload === 'string' ? { domain: payload } : payload),
      // 带套餐业务意图的请求由分区协调器原子处理；旧请求继续使用成熟域名接口。
      createWithFlow: async (payload: DomainCreatePayload & {
        idempotencyKey?: string;
        packageSelection?: unknown;
      }): Promise<DomainSaveResponse> => {
        if ('packageSelection' in payload || 'idempotencyKey' in payload) {
          const response = await http.post<DomainSaveResponse, typeof payload>(
            '/api/v1/console/domain-plans/domains',
            payload
          );
          return { ...response, certificateBlocksDomainSave: false };
        }
        const body = payload.accessMode === 'REDIRECT_301'
          ? { domain: payload.domain, accessMode: payload.accessMode, redirectTarget: payload.redirectTarget }
          : { domain: payload.domain };
        const domain = await http.post<Domain>('/api/v1/domains', body);
        return {
          domain,
          certificateBlocksDomainSave: false
        };
      },
      update: (domainId: number, domain: string) => http.put<Domain>(`/api/v1/domains/${domainId}`, { domain }),
      delete: (domainId: number) => http.delete<void>(`/api/v1/domains/${domainId}`),
      pause: (domainId: number) => http.post<Domain>(`/api/v1/domains/${domainId}/pause`),
      enable: (domainId: number) => http.post<Domain>(`/api/v1/domains/${domainId}/enable`)
    },
    rootRedirects: {
      get: (domainId: number) => http.get<RootRedirectSnapshot>(`/api/v1/console/root-redirects/${domainId}`),
      recheck: (domainId: number) => http.post<RootRedirectRecheckResponse>(`/api/v1/console/root-redirects/${domainId}/recheck`)
    },
    domainDrafts: {
      list: () => http.get<DomainDraft[]>('/api/v1/console/domain-drafts'),
      create: (body: DomainDraftCreatePayload) =>
        http.post<DomainDraft, DomainDraftCreatePayload>('/api/v1/console/domain-drafts', body)
    },
    dns: {
      get: (domainId: number) => http.get<CustomerDnsResponse>(`/api/v1/domains/${domainId}/dns`),
      recheck: (domainId: number) => http.post<CustomerDnsResponse>(`/api/v1/domains/${domainId}/dns/recheck`)
    },
    origins: {
      list: (domainId: number) => http.get<Origin[]>(`/api/v1/domains/${domainId}/origins`),
      create: (body: Partial<Origin>) => http.post('/api/v1/origins', body),
      update: (originId: number, body: Partial<Origin>) => http.put(`/api/v1/origins/${originId}`, body),
      delete: (originId: number) => http.delete(`/api/v1/origins/${originId}`),
      check: (originId: number) => http.post(`/api/v1/origins/${originId}/check`),
      reports: (originId: number) => http.get<OriginCheckReport[]>(`/api/v1/origins/${originId}/check-reports`)
    },
    certificates: {
      list: () => http.get<Certificate[]>('/api/v1/certificates'),
      get: (certificateId: number) => http.get<Certificate>(`/api/v1/certificates/${certificateId}`),
      upload: (body: UploadCertificateRequest) => http.post<Certificate>('/api/v1/certificates/upload', body),
      decision: (body: CertificateDecisionRequest) =>
        http.post<CertificateDecision, CertificateDecisionRequest>('/api/v1/certificates/decision', body),
      update: (certificateId: number, body: UpdateCertificateRequest) =>
        http.put<Certificate, UpdateCertificateRequest>(`/api/v1/certificates/${certificateId}`, body),
      managed: (body: ManagedCertificateRequest) =>
        http.post<AcmeOrderResponse, ManagedCertificateRequest>('/api/v1/certificates/managed', body),
      regenerate: (certificateId: number, highRiskAction: HighRiskActionRequest) =>
        http.post<AcmeOrderResponse, HighRiskActionRequest>(
          `/api/v1/certificates/${certificateId}/acme/regenerate`,
          highRiskAction
        ),
      recheck: (certificateId: number) =>
        http.post<AcmeOrderResponse>(`/api/v1/certificates/${certificateId}/acme/recheck`),
      matches: (certificateId: number, domainId: number) =>
        http.get<CertificateMatchResponse>(`/api/v1/certificates/${certificateId}/matches`, { params: { domainId } }),
      coverage: (certificateId: number) =>
        http.get<CertificateCoverageResponse>(`/api/v1/certificates/${certificateId}/coverage`),
      bind: (certificateId: number, domainId: number, highRiskAction?: HighRiskActionRequest) =>
        highRiskAction
          ? http.post<Certificate>(`/api/v1/certificates/${certificateId}/domains/${domainId}/bind`, highRiskAction)
          : http.post<Certificate>(`/api/v1/certificates/${certificateId}/domains/${domainId}/bind`),
      unbind: (certificateId: number, domainId: number) =>
        http.delete<Certificate>(`/api/v1/certificates/${certificateId}/domains/${domainId}`),
      download: (certificateId: number, body: CertificateDownloadRequest) =>
        http.post<Blob, CertificateDownloadRequest>(
          `/api/v1/certificates/${certificateId}/download`,
          body,
          { responseType: 'blob' }
        ),
      renew: (certificateId: number, highRiskAction?: HighRiskActionRequest) =>
        highRiskAction
          ? http.post(`/api/v1/certificates/${certificateId}/renew`, highRiskAction)
          : http.post<AcmeOrderResponse>(`/api/v1/certificates/${certificateId}/renew`),
      autoReissue: (certificateId: number, body: CertificateAutoReissueRequest = {}) =>
        http.post<AcmeOrderResponse, CertificateAutoReissueRequest>(
          `/api/v1/certificates/${certificateId}/acme/auto-reissue`,
          body
        ),
      deleteDomain: (domainId: number, highRiskAction: HighRiskActionRequest) =>
        http.delete<void>(`/api/v1/certificates/domains/${domainId}`, { data: highRiskAction }),
      delete: (certificateId: number, highRiskAction?: HighRiskActionRequest) =>
        highRiskAction
          ? http.delete<void>(`/api/v1/certificates/${certificateId}`, { data: highRiskAction })
          : http.delete<void>(`/api/v1/certificates/${certificateId}`)
    },
    cache: {
      profile: (domainId: number) => http.get<CacheProfile>(`/api/v1/cache/domains/${domainId}/profile`),
      operations: (domainId: number) =>
        http.get<CacheOperation[]>(`/api/v1/cache/domains/${domainId}/operations`),
      // Console 0.1.137; 2026-10-07 01:19:11 CST; parent 941991113bff4331e0f76f14ba9e8fd7a2821d2c.
      // CacheProfileSettingsRequest accepts these seven fields only; GET metadata/rules are read-only here.
      updateProfile: (domainId: number, body: CacheProfile) =>
        http.put(`/api/v1/cache/domains/${domainId}/profile`, {
          cacheEnabled: body.cacheEnabled,
          defaultTtlSeconds: body.defaultTtlSeconds,
          browserTtlSeconds: body.browserTtlSeconds,
          respectOriginCacheControl: body.respectOriginCacheControl,
          queryCacheEnabled: body.queryCacheEnabled,
          ignoreQueryOrder: body.ignoreQueryOrder,
          staleIfErrorEnabled: body.staleIfErrorEnabled
        }),
      createRule: (domainId: number, body: CacheRule) =>
        http.post(`/api/v1/cache/domains/${domainId}/rules`, body),
      purge: (domainId: number, body: Record<string, unknown>) =>
        http.post(`/api/v1/cache/domains/${domainId}/purge`, body),
      preload: (domainId: number, body: Record<string, unknown>) =>
        http.post(`/api/v1/cache/domains/${domainId}/preload`, body)
    },
    publish: {
    // 配置包生成二次确认兼容，更新时间：2026-08-30 22:59 CST。自动发布可传入当前域名的明确确认信息；旧页面未传参数时由 API 层补齐严格限定为当前域名的确认内容，避免构建失败且不扩大操作范围。
    generatePackage: (domainId: number, highRiskAction?: HighRiskActionRequest) =>
      http.post<PublishPackage, HighRiskActionRequest>(
        `/api/v1/domains/${domainId}/config-packages`,
        highRiskAction || {
          acknowledged: true,
          actionLabel: '生成当前域名配置包',
          reason: `用户生成域名 ID ${domainId} 的配置包`,
          impactPreview: [`仅生成域名 ID ${domainId} 的配置包`, '不会修改或发布其他域名配置']
        }
      ),
      packages: (domainId: number) => http.get<PublishPackage[]>(`/api/v1/domains/${domainId}/config-packages`),
      createDraft: (domainId: number, comment: string) =>
        http.post(`/api/v1/domains/${domainId}/publish-drafts`, { comment }),
      history: (domainId: number) => http.get<PublishHistory[]>(`/api/v1/domains/${domainId}/publish-history`),
      releases: (domainId: number) => http.get<PublishRelease[]>(`/api/v1/domains/${domainId}/publish-releases`),
      release: (releaseId: number) => http.get<PublishRelease>(`/api/v1/publish-releases/${releaseId}`),
      activeConfig: (domainId: number) => http.get<DomainActiveConfig>(`/api/v1/domains/${domainId}/active-config`)
    },
    logs: {
      trafficEvidence: (params: Record<string, unknown>) =>
        http.get<TrafficEvidence>('/api/v1/log-center/traffic-evidence', { params }),
      access: (params: Record<string, unknown>) =>
        http.get<AccessSummary>('/api/v1/log-center/access', { params }),
      topIps: (params: Record<string, unknown>) =>
        http.get<TopIpStat[]>('/api/v1/log-center/top-ips', { params }),
      nodeAnalytics: (params: Record<string, unknown>) =>
        http.get<NodeAnalyticsResponse>('/api/v1/log-center/node-analytics', { params }),
      statusCodes: (params: Record<string, unknown>) =>
        http.get<StatusCodeStat[]>('/api/v1/log-center/status-codes', { params }),
      countries: (params: Record<string, unknown>) =>
        http.get<CountryStat[]>('/api/v1/log-center/countries-resolved', { params }),
      publish: (params: Record<string, unknown>) =>
        http.get<LogPage<PublishLog>>('/api/v1/log-center/publish', { params }),
      security: (params: Record<string, unknown>) =>
        http.get<SecurityLog[]>('/api/v1/log-center/security', { params }),
      operations: (params: Record<string, unknown>) =>
        http.get<LogPage<OperationLog>>('/api/v1/log-center/operations', { params }),
      export: (body: Record<string, unknown>) => http.post<ExportTask>('/api/v1/log-center/export', body)
    },
    traffic: {
      overview: (params: Record<string, unknown>) =>
        http.get('/api/v1/traffic-monitoring/overview', { params }),
      export: (body: Record<string, unknown>) =>
        http.post<ExportTask>('/api/v1/traffic-monitoring/export', body)
    },
    security: {
      // 策略工作区接口，更新时间：2026-09-05 CST。策略是主对象，域名仅作为一对多绑定目标。
      strategies: () => http.get<SecurityStrategy[]>('/api/v1/security/strategies'),
      createStrategy: (body: { name: string; domainIds: number[] }) =>
        http.post<SecurityStrategyMutationResponse>('/api/v1/security/strategies', body),
      updateStrategy: (strategyId: number, body: { name: string; domainIds: number[] }) =>
        http.put<SecurityStrategyMutationResponse>(`/api/v1/security/strategies/${strategyId}`, body),
      updateStrategyConfiguration: (strategyId: number, body: SecurityStrategyConfiguration) =>
        http.put<SecurityStrategyMutationResponse>(
          `/api/v1/security/strategies/${strategyId}/configuration`, body
        ),
      deleteStrategy: (strategyId: number, confirmName: string) =>
        http.post<SecurityStrategyDeleteResponse>(`/api/v1/security/strategies/${strategyId}/delete`, {
          confirmName
        }),
      updateStrategyPublishStatus: (strategyId: number, body: { status: string; message?: string }) =>
        http.put<SecurityStrategy>(`/api/v1/security/strategies/${strategyId}/publish-status`, body),
      publishStrategyDomain: (domainId: number, body: { packageId: number; reason: string }) =>
        http.post<PublishRelease>(`/api/v1/security/strategies/domains/${domainId}/publish`, body),
      simulateStrategy: (strategyId: number, body: SecurityPolicySimulationRequest) =>
        http.post<SecurityStrategySimulationResponse, SecurityPolicySimulationRequest>(
          `/api/v1/security/strategies/${strategyId}/simulate`, body
        ),
      ipCollections: () =>
        http.get<IpCollection[]>('/api/v1/security/ip-collections'),
      createIpCollection: (body: IpCollectionCreateRequest) =>
        http.post<IpCollection, IpCollectionCreateRequest>('/api/v1/security/ip-collections', body),
      deleteIpCollection: (collectionId: number) =>
        http.delete<void>(`/api/v1/security/ip-collections/${collectionId}`),
      domainIpCollections: (domainId: number) => {
        void domainId;
        return Promise.resolve(emptyIpCollectionBinding());
      },
      updateDomainIpCollections: (domainId: number, body: IpCollectionBindingRequest) => {
        void domainId;
        void body;
        return Promise.resolve(emptyIpCollectionBinding());
      },
      ipRules: (domainId: number) =>
        http.get<SecurityIpRule[]>(`/api/v1/security/domains/${domainId}/ip-rules`),
      createIpRule: (domainId: number, body: SecurityIpRuleRequest) =>
        http.post(`/api/v1/security/domains/${domainId}/ip-rules`, body),
      deleteIpRule: (domainId: number, ruleId: number) =>
        http.delete(`/api/v1/security/domains/${domainId}/ip-rules/${ruleId}`),
      countryPolicy: (domainId: number) =>
        http.get<CountryPolicy>(`/api/v1/security/domains/${domainId}/country-policy`),
      updateCountryPolicy: (domainId: number, body: CountryPolicy) =>
        http.put(`/api/v1/security/domains/${domainId}/country-policy`, body),
      // 只读判断访问 IP 会命中哪一层规则，不保存策略，也不触发节点发布。
      simulate: (domainId: number, body: SecurityPolicySimulationRequest) =>
        http.post<SecurityPolicySimulationResponse, SecurityPolicySimulationRequest>(
          `/api/v1/security/domains/${domainId}/simulate`,
          body
        )
    }
  };
}

function serviceStatusToPlan(status: ServiceStatus): ConsolePlanResponse {
  const limit = numberOrNull(status.rootDomainLimit ?? status.totalDomainLimit);
  const used = numberOrZero(status.rootDomainUsed ?? status.totalDomainUsed);
  const remaining = numberOrNull(status.rootDomainRemaining ?? status.totalDomainRemaining);
  const canModifyBusiness = status.canModifyBusiness !== false;
  return {
    currentPlan: {
      status: normalizePlanStatus(status.planStatus, status.serviceStatus),
      snapshot: {
        planCode: normalizePlanCode(status.planName),
        planName: status.planName || '当前套餐',
        planType: status.planType || guessPlanType(status.planName),
        quotaMode: status.quotaMode || 'ROOT_DOMAIN',
        quotaLimit: limit,
        periodMode: 'TRIAL_FLEX',
        capabilities: {
          cdnAcceleration: true,
          managedCertificate: true,
          waf: true,
          cacheRules: true,
          logExport: true
        }
      },
      activatedAt: status.serviceStartAt,
      expiresAt: status.serviceExpireAt
    },
    quota: {
      mode: status.quotaMode || 'ROOT_DOMAIN',
      used,
      limit,
      remaining
    },
    servicePolicy: {
      accelerationEnabled: canModifyBusiness,
      preserveBusinessConfiguration: true,
      cdnSwitchMutable: canModifyBusiness,
      reasonCode: canModifyBusiness ? undefined : serviceStatusReason(status.serviceStatus)
    },
    writeAccess: {
      canModifyBusiness,
      code: canModifyBusiness ? 'OK' : status.serviceStatus,
      message: status.notice,
      notice: status.notice
    }
  };
}

function fallbackPlan(): NonNullable<ConsolePlanResponse['currentPlan']> {
  return {
    status: 'NO_PLAN',
    snapshot: {
      planCode: 'FREE',
      planName: '免费套餐',
      planType: 'FREE',
      quotaMode: 'ROOT_DOMAIN',
      quotaLimit: null,
      periodMode: 'TRIAL_FLEX',
      capabilities: {
        cdnAcceleration: true,
        managedCertificate: true,
        waf: true,
        cacheRules: true,
        logExport: true
      }
    }
  };
}

function normalizePlanStatus(planStatus: ServiceStatus['planStatus'], serviceStatus: string) {
  if (planStatus) {
    return planStatus;
  }
  if (serviceStatus === 'ACTIVE' || serviceStatus === 'FREE_ACTIVE') {
    return 'ACTIVE';
  }
  if (serviceStatus === 'PAUSED') {
    return 'PAUSED';
  }
  if (serviceStatus === 'EXPIRED') {
    return 'EXPIRED';
  }
  return 'NO_PLAN';
}

function normalizePlanCode(planName?: string) {
  if (!planName) {
    return 'CURRENT';
  }
  return planName.toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'CURRENT';
}

function guessPlanType(planName?: string) {
  return planName?.includes('免费') ? 'FREE' : 'ENTERPRISE';
}

function serviceStatusReason(serviceStatus: string) {
  if (serviceStatus === 'PAUSED') {
    return 'PLAN_PAUSED';
  }
  if (serviceStatus === 'EXPIRED') {
    return 'PLAN_EXPIRED';
  }
  return 'PLAN_REQUIRED';
}

function numberOrZero(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function numberOrNull(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export type ConsoleApi = ReturnType<typeof createConsoleApi>;
